import Fuse from 'fuse.js';

import { type Conversation } from '../../types';

/**
 * Fuse.js configuration for conversation search
 * Aligned with existing AFM patterns (command palette, template search)
 */
const FUSE_CONVERSATION_SEARCH_OPTIONS = {
	// 0.3 threshold for better fuzzy matching (30% similarity required)
	threshold: 0.3,

	// Search fields with weights (higher weight = more important)
	keys: [
		{
			name: 'name',
			weight: Math.pow(2, 5), // 32 - conversation title is most important
		},
		{
			name: 'agent.agent_name',
			weight: Math.pow(2, 4), // 16 - agent name is important
		},
		{
			name: 'lastMessage',
			weight: Math.pow(2, 3), // 8 - last message is secondary
		},
	],

	// Include score for sorting by relevance
	includeScore: true,

	// Allow sorting by relevance score
	shouldSort: true,

	// Distance for fuzzy matching
	distance: 1000,

	// Ignore field length normalization for better results
	ignoreFieldNorm: true,
};

let cachedFuseIndex: Fuse<Conversation> | null = null;
let cachedConversationIds: string[] = [];

/**
 * Creates or returns cached Fuse index if the conversations haven't changed
 */
const getFuseIndex = (conversations: Conversation[]) => {
	const currentIds = conversations.map((c) => c.id);
	const idsChanged =
		currentIds.length !== cachedConversationIds.length ||
		currentIds.some((id, i) => id !== cachedConversationIds[i]);

	if (!cachedFuseIndex || idsChanged) {
		cachedFuseIndex = new Fuse(conversations, FUSE_CONVERSATION_SEARCH_OPTIONS);
		cachedConversationIds = currentIds;
	}

	return cachedFuseIndex;
};

export const filterAndSortConversations = (
	conversations: Conversation[],
	filters: {
		searchValue: string;
		direction: 'asc' | 'desc';
		sortPinned?: boolean;
	},
): Conversation[] => {
	const trimmedSearchValue = filters.searchValue.trim();
	const sortPinned = filters.sortPinned === true;

	const getTimestamp = (timestamp?: string | null) => {
		if (!timestamp) {
			return undefined;
		}

		const time = new Date(timestamp).getTime();

		return Number.isNaN(time) ? undefined : time;
	};

	const compareTimestamps = (
		aTimestamp: string | null | undefined,
		bTimestamp: string | null | undefined,
		direction: 'asc' | 'desc',
	) => {
		const aTime = getTimestamp(aTimestamp);
		const bTime = getTimestamp(bTimestamp);

		if (aTime === undefined && bTime === undefined) {
			return 0;
		}
		if (aTime === undefined) {
			return 1;
		}
		if (bTime === undefined) {
			return -1;
		}

		return direction === 'asc' ? aTime - bTime : bTime - aTime;
	};

	const sortByPinnedThenTime = (a: Conversation, b: Conversation) => {
		const aIsPinned = Boolean(a.isPinned);
		const bIsPinned = Boolean(b.isPinned);

		if (sortPinned && aIsPinned !== bIsPinned) {
			return aIsPinned ? -1 : 1;
		}

		if (sortPinned && aIsPinned && bIsPinned) {
			const pinnedSort = compareTimestamps(a.pinnedAt, b.pinnedAt, 'desc');

			if (pinnedSort !== 0) {
				return pinnedSort;
			}
		}

		return compareTimestamps(a.lastMessageTimestamp, b.lastMessageTimestamp, filters.direction);
	};

	const sortByPinnedOnly = (a: Conversation, b: Conversation) => {
		if (!sortPinned) {
			return 0;
		}

		if (a.isPinned === b.isPinned) {
			return 0;
		}
		return a.isPinned ? -1 : 1;
	};

	// Return all non-empty conversations if no search term
	if (!trimmedSearchValue) {
		return conversations.filter((c) => !!c && !c.isEmpty).sort(sortByPinnedThenTime);
	}

	// Filter out empty conversations
	const nonEmptyConversations = conversations.filter((c) => !!c && !c.isEmpty);

	// Get or create Fuse index (cached if conversations haven't changed)
	const fuse = getFuseIndex(nonEmptyConversations);
	const results = fuse.search(trimmedSearchValue);

	// Extract conversation objects from Fuse results. Keep Fuse relevance within pinned/unpinned groups.
	return results.map((result) => result.item).sort(sortByPinnedOnly);
};
